<template>
<main class="container-1">

    <div class="container-header">
        <Breadcrumb v-bind:links="routesBread"></Breadcrumb>
        <TabNav :links="routesNav" />
    </div>

    <div class="container-body">

        <p class="external-layers__intro">
            Archivos SHP, KMZ y GeoJSON que los usuarios subieron a los mapas.
            Descárgalos para republicarlos como servicio, o elimínalos si ya no se usan.
        </p>

        <div v-if="feedback" class="order-feedback" role="status">{{ feedback }}</div>

        <div class="table-toolbar">
            <div class="toolbar-left">
                <label>
                    Mostrar registros:
                    <select v-model="pageSize" @change="currentPage = 1">
                        <option :value="10">10</option>
                        <option :value="15">15</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                    </select>
                </label>
            </div>
            <div class="toolbar-right">
                <label>
                    Buscar:
                    <input
                        type="text"
                        v-model="searchQuery"
                        placeholder="Buscar archivo, mapa o usuario..."
                    />
                </label>
            </div>
        </div>

        <table class="external-layers-table">
            <thead>
                <tr>
                    <th>Archivo</th>
                    <th>Mapa</th>
                    <th>Usuario</th>
                    <th>Fecha de subida</th>
                    <th class="col-size">Peso</th>
                    <th>Acciones</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="item in pagedLayers" :key="item.id" :class="{ 'row--orphan': item.owner_missing }">
                    <td class="col-name">{{ item.name }}</td>
                    <td>{{ item.map_name }}</td>
                    <td>
                        <span :class="{ 'external-layers__owner--missing': item.owner_missing }">
                            {{ item.user_name }}
                        </span>
                    </td>
                    <td class="col-date">{{ dateTimeFormatter(item.uploaded_at) }}</td>
                    <td class="col-size">{{ formatSize(item.size_bytes) }}</td>
                    <td class="action-cell">
                        <div class="action-cell__buttons">
                            <button type="button" class="btn-action btn-ver" @click="download(item)">
                                Descargar
                            </button>
                            <button type="button" class="btn-action btn-delete" @click="askDelete(item)">
                                Eliminar
                            </button>
                        </div>
                    </td>
                </tr>
                <tr v-if="!filteredLayers.length">
                    <td colspan="6" class="empty-cell">
                        <span v-if="searchQuery">No hay capas externas que coincidan con la búsqueda</span>
                        <span v-else>No hay capas externas</span>
                    </td>
                </tr>
            </tbody>
        </table>

        <Pagination
        v-show="totalPages!=0"
        v-bind:totalPages="totalPages"
        v-bind:currentPage="currentPage"
        v-bind:pageSize="pageSize"
        v-on:pagechanged="pageChangedClick"
        >
        </Pagination>

    </div>

    <div v-if="pendingDelete" class="modal-backdrop" @click.self="pendingDelete = null">
        <section class="modal-window modal-confirm" role="dialog" aria-modal="true">
            <header class="modal-header">
                <div>
                    <span class="modal-category">Confirmación</span>
                    <h3>Eliminar capa externa</h3>
                </div>
                <button type="button" class="modal-close" aria-label="Cerrar" @click="pendingDelete = null">×</button>
            </header>
            <div class="modal-body">
                <p class="modal-question">¿Realmente deseas eliminar {{ pendingDelete.name }}?</p>
                <p class="modal-note">
                    Se borra el archivo del servidor y no se puede recuperar.
                    Si te sirve, descárgalo antes.
                </p>
            </div>
            <footer class="modal-footer">
                <button type="button" class="modal-button" @click="pendingDelete = null">Cancelar</button>
                <button
                    type="button"
                    class="modal-button modal-button-delete"
                    :disabled="deleting"
                    @click="confirmDelete"
                >
                    {{ deleting ? 'Eliminando...' : 'Sí, eliminar' }}
                </button>
            </footer>
        </section>
    </div>

</main>
</template>

<script>
// Administración de capas externas (QA-144).
//
// Vive dentro de Capas y no como módulo propio: SENAPRED descartó darle una
// sección aparte del menú por ser algo puntual, y la pensó como herramienta de
// aseo —ver qué subió cada usuario, rescatar lo que sirva y borrar el resto—.
import Breadcrumb from '@/components/Breadcrumb.vue'
import TabNav from '@/components/TabNav.vue'
import Pagination from '@/components/Pagination.vue'
import { formatFileSize } from '@/services/layers/fileSize.js'

import { formatter } from '@/components/mixin/formatter.js'
import { pagination } from '@/components/mixin/pagination.js'

export default {
    name: 'ExternalLayers',
    mixins: [formatter, pagination],
    components: { Breadcrumb, TabNav, Pagination },
    data() {
        return {
            layers: [],
            searchQuery: '',
            pendingDelete: null,
            deleting: false,
            feedback: '',
        }
    },
    computed: {
        routesNav() {
            return [
                { name: 'Opciones' },
                { name: 'Crear capa', path: '/capas/nueva-capa' },
                { name: 'Ver categorías', path: '/capas/categorias-de-capas' },
                { name: 'Ver capas externas', path: '/capas/capas-externas' },
            ]
        },
        routesBread() {
            return [
                { name: 'Capas', path: '/capas' },
                { name: 'Capas externas' },
            ]
        },
        filteredLayers() {
            const query = this.searchQuery.trim().toLowerCase()
            if (!query) return this.layers
            return this.layers.filter(item =>
                [item.name, item.map_name, item.user_name]
                    .some(field => (field || '').toLowerCase().includes(query))
            )
        },
        pagedLayers() {
            const start = (this.currentPage - 1) * this.pageSize
            return this.filteredLayers.slice(start, start + this.pageSize)
        },
    },
    watch: {
        filteredLayers() {
            this.totalPages = Math.ceil(this.filteredLayers.length / this.pageSize)
            if (this.currentPage > this.totalPages) this.currentPage = 1
        },
    },
    mounted() {
        this.fetchLayers()
    },
    methods: {
        formatSize: formatFileSize,
        adminUrl(suffix) {
            return `${this.$store.getters.backendurl}api/v1/uploads/admin${suffix || ''}`
        },
        fetchLayers() {
            this.$http.get(this.adminUrl())
                .then(response => {
                    this.layers = response.data || []
                    this.totalPages = Math.ceil(this.layers.length / this.pageSize)
                })
                .catch(error => {
                    console.log(error)
                    this.feedback = 'No fue posible obtener las capas externas.'
                })
        },
        download(item) {
            // El backend responde 410 si el documento sobrevivió al archivo, así
            // que conviene enterarse acá en vez de abrir una pestaña en blanco.
            this.$http.get(this.adminUrl(`/${item.id}/download`), { responseType: 'blob' })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]))
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', item.name)
                    document.body.appendChild(link)
                    link.click()
                    link.remove()
                    window.URL.revokeObjectURL(url)
                })
                .catch(error => {
                    console.log(error)
                    this.feedback = `No fue posible descargar ${item.name}. Puede que el archivo ya no esté en el servidor.`
                })
        },
        askDelete(item) {
            this.feedback = ''
            this.pendingDelete = item
        },
        confirmDelete() {
            const item = this.pendingDelete
            this.deleting = true
            this.$http.delete(this.adminUrl(`/${item.id}`))
                .then(() => {
                    this.layers = this.layers.filter(row => row.id !== item.id)
                    this.feedback = `${item.name} fue eliminado.`
                })
                .catch(error => {
                    console.log(error)
                    this.feedback = `No fue posible eliminar ${item.name}.`
                })
                .then(() => {
                    this.deleting = false
                    this.pendingDelete = null
                })
        },
        pageChangedClick(page) {
            this.currentPage = page
        },
    },
}
</script>

<style lang="scss" scoped>
// Mismo lenguaje visual que views/categories/Categories.vue: la barra de
// herramientas y la tabla se estilan por vista en este proyecto, no hay una
// clase compartida que heredar.
.external-layers__intro {
    margin: 0 0 1.2em;
    color: var(--color-graphite);
    font-size: 0.9em;
}

.table-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 1em;
    margin-bottom: 1em;
}

.toolbar-left,
.toolbar-right {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 0.75em;
}

.table-toolbar label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}

.toolbar-left select {
    width: 110px;
}

.toolbar-right {
    flex: 1;
    justify-content: flex-start;
}

.toolbar-right input {
    width: 100%;
    max-width: 260px;
    min-width: 180px;
}

.external-layers-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1em;
}

.external-layers-table th,
.external-layers-table td {
    padding: 0.6em 0.8em;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
    text-align: left;
    vertical-align: middle;
    font-size: 0.875em;
}

.external-layers-table thead th {
    background: var(--color-navy);
    color: #fff;
    white-space: nowrap;
    font-weight: 600;
}

.external-layers-table tbody tr:hover {
    background: rgba(59, 110, 148, 0.06);
}

.col-name {
    font-weight: 600;
    word-break: break-all;
}

.col-date {
    white-space: nowrap;
}

// El peso es una magnitud: alineado a la derecha se comparan las filas de un
// vistazo, que es lo que se hace en esta pantalla para decidir qué borrar.
.col-size {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}

.empty-cell {
    padding: 2.5em 0.8em;
    color: var(--color-ghost);
    text-align: center;
}

// Un archivo sin dueño no es un error de datos: es el que hay que revisar.
.external-layers__owner--missing {
    font-style: italic;
    color: var(--color-ghost);
}

.row--orphan {
    background: rgba(232, 115, 12, 0.05);
}

.action-cell {
    white-space: nowrap;
}

.action-cell__buttons {
    display: flex;
    flex-wrap: nowrap;
    gap: 0.4em;
    align-items: center;
}

.btn-action {
    display: inline-flex;
    align-items: center;
    gap: 0.25em;
    padding: 0.2em 0.55em;
    border: 0;
    border-radius: 3px;
    font-family: inherit;
    font-size: 0.82em;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    transition: opacity 0.12s;

    &:hover { opacity: 0.8; }
}

.btn-ver    { background: #e8f4fd; color: var(--color-navy); }
.btn-delete { background: #fdf2f2; color: #922b21; }

// Confirmación de borrado: mismas clases y estilo que components/UserDeleteModal.vue.
.modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2em;
    background: color-mix(in srgb, var(--color-dark) 55%, transparent);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
}

.modal-window {
    width: 100%;
    max-width: 560px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid var(--color-fog);
    border-radius: 6px;
    background: var(--color-white);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1em;
    padding: 1em 1.3em;
    border-bottom: 1px solid var(--color-fog);
    background: var(--color-navy);
    color: var(--color-white);
}

.modal-header h3 {
    margin: 0.15em 0 0;
    color: var(--color-white);
    font-size: 1.1em;
}

.modal-category {
    color: var(--color-orange);
    font-size: 0.72em;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.modal-close {
    width: 29px;
    height: 29px;
    border: 1px solid var(--color-steel);
    border-radius: 4px;
    background: transparent;
    color: var(--color-white);
    font-size: 1.25em;
    cursor: pointer;

    &:hover { border-color: var(--color-orange); }
}

.modal-body {
    padding: 1.3em;
}

.modal-question {
    margin: 0 0 1rem;
    color: var(--color-graphite);
    font-size: 0.95rem;
    font-weight: 600;
}

.modal-note {
    margin: 0;
    color: var(--color-ghost);
    font-size: 0.85rem;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.7em;
    padding: 0.9em 1.3em;
    border-top: 1px solid var(--color-fog);
    background: var(--color-mist);
}

.modal-button {
    padding: 0.5em 1em;
    border: 1px solid var(--color-steel);
    border-radius: 4px;
    background: var(--color-white);
    color: var(--color-steel);
    font-family: inherit;
    font-size: 0.82em;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: var(--color-steel);
        color: var(--color-white);
    }

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
}

.modal-button-delete {
    border-color: var(--color-danger);
    color: var(--color-danger);

    &:hover:not(:disabled) {
        background: var(--color-danger);
        border-color: var(--color-danger);
        color: var(--color-white);
    }
}
</style>
